// vim: set ts=2 sts=2 sw=2 et:

import type { Equipment, Lift } from "../../../types/dataTypes";
import type { WrpfRecordCategory } from "../../handoff/types";
import { getLiftProgress, LiftProgress } from "../../live/liftProgress";
import type { JudgeLights, PlatformLiveAttempt, PlatformLiveBroadcastInfo } from "../../live/platformLiveProtocol";
import type { PlatformRef } from "../../types";
import { getRecordCategoryPresentation } from "../../records/recordCategories";
import { countryLabel } from "./countryLabel";

export interface RecordView {
  category: WrpfRecordCategory;
  label: string;
  color: string;
  textColor: string;
}

export interface OverlayAthleteView {
  key: string;
  entryKey: number;
  name: string;
  details: Array<string>;
  origin: Array<string>;
  lift: Lift;
  liftLabel: string;
  attemptNumber: number;
  attemptLabel: string;
  weightLabel: string;
  record: RecordView | null;
  progress: LiftProgress;
}

export interface OverlayView {
  tournamentName: string;
  sessionLabel: string;
  athlete: OverlayAthleteView | null;
  waitingForNext: boolean;
  lights: JudgeLights;
  result: "good" | "noLift" | null;
  clockSeconds: number;
  clockRunning: boolean;
}

export const LIFT_LABELS: Readonly<Record<Lift, string>> = {
  S: "Sentadilla",
  B: "Banco Plano",
  D: "Peso Muerto",
};

const RECORD_NAMES: Readonly<Record<WrpfRecordCategory, string>> = {
  REGIONAL: "regional",
  NATIONAL: "nacional",
  IBEROAMERICAN: "iberoamericano",
  WORLD: "mundial",
};

export const formatKg = (weightKg: number): string =>
  Number.isInteger(weightKg) ? String(weightKg) : weightKg.toLocaleString("es-AR", { maximumFractionDigits: 1 });

export const getRecordView = (category: WrpfRecordCategory | undefined): RecordView | null => {
  if (category === undefined) return null;
  const presentation = getRecordCategoryPresentation(category);
  return {
    category,
    label: `Récord ${RECORD_NAMES[category]}`,
    color: presentation.gradientEnd,
    textColor: presentation.textColor,
  };
};

const getEquipmentLabel = (equipment: Equipment | undefined): string | null => {
  if (equipment === "Bare") return "RAW";
  if (equipment === "Sleeves") return "Rodilleras";
  if (equipment === "Wraps") return "Vendas";
  return equipment ?? null;
};

const getWeightClassLabel = (weightClassKg: string | undefined): string | null => {
  const value = weightClassKg?.trim().replace(/\s*kg$/i, "");
  if (!value) return null;
  if (value.endsWith("+")) return `+${value.slice(0, -1)} kg`;
  return `${value.startsWith("-") ? `−${value.slice(1)}` : `−${value}`} kg`;
};

const ordinal = (attemptNumber: number): string => `${attemptNumber}º intento`;

export const buildAthleteView = (
  attempt: PlatformLiveAttempt,
  result: "good" | "noLift" | null,
): OverlayAthleteView => {
  const meta = attempt.athleteMeta;
  const details = [
    meta?.ageDivision?.trim() || null,
    getWeightClassLabel(meta?.weightClassKg),
    getEquipmentLabel(meta?.equipment),
    meta !== undefined && meta.age > 0 ? `${meta.age} años` : null,
  ].filter((detail): detail is string => detail !== null);
  const origin = [
    countryLabel(meta?.location?.country),
    meta?.location?.state || meta?.location?.city || null,
    meta?.team?.trim() || null,
  ].filter((value): value is string => value !== null);
  return {
    key: attempt.key,
    entryKey: attempt.entryId,
    name: attempt.lifterName,
    details,
    origin,
    lift: attempt.lift,
    liftLabel: LIFT_LABELS[attempt.lift],
    attemptNumber: attempt.attempt,
    attemptLabel: ordinal(attempt.attempt),
    weightLabel: formatKg(attempt.weightKg),
    record: attempt.recordAttempt === true ? getRecordView(attempt.recordCategory) : null,
    progress: getLiftProgress(attempt, { attemptKey: attempt.key, result }),
  };
};

/** Sin nombre publicado (operador con una versión anterior o meet sin nombre) se muestra la federación. */
export const FALLBACK_TOURNAMENT_NAME = "WRPF Argentina";

export const getSessionLabel = (platformRef: PlatformRef): string =>
  `Día ${platformRef.day}, tarima ${platformRef.platformId}`;

export interface LiveOverlayInput {
  attempt: PlatformLiveAttempt | null;
  judgeLights: JudgeLights;
  remainingSeconds: number | null;
  waitingForNext: boolean;
  revealedResult: "good" | "noLift" | null;
  broadcast: PlatformLiveBroadcastInfo;
  platformRef: PlatformRef;
}

// Vista del overlay a partir de lo que publica el operador por el live.
export const buildLiveOverlayView = (input: LiveOverlayInput): OverlayView => ({
  tournamentName: input.broadcast.meetName?.trim() || FALLBACK_TOURNAMENT_NAME,
  sessionLabel: getSessionLabel(input.platformRef),
  athlete: input.attempt === null ? null : buildAthleteView(input.attempt, input.revealedResult),
  waitingForNext: input.waitingForNext,
  lights: input.judgeLights,
  result: input.revealedResult,
  clockSeconds: input.remainingSeconds === null ? 60 : Math.max(0, input.remainingSeconds),
  clockRunning: input.remainingSeconds !== null,
});
